import React, { Fragment, PureComponent } from 'react';
import { ExchangeRates, Rate } from '~/Api/User/ExchangeRate';
import formatFactor from '~/ExchangeRate/formatFactor';
import isFactorValid from '~/ExchangeRate/isFactorValid';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import Button from '~/UI/Button';
import Input from '~/UI/Input/Input';

import styles from './styles.less';

type Factor = { raw: string; parsed: number | null; isValid: boolean };

interface Props {
	selectedRates: ExchangeRates;
	changedRates: {
		[key: string]: Factor;
	};
	baseCurrency: string | null;
	styles: {
		input: string;
		inputSuffixButton: string;
		paragraph: string;
		inputItem: string;
		inputSuffix: string;
		separator: string;
	};
	onChange(value: { [key: string]: Factor }): void;
	onBaseCurrencyChange(currency: string): void;
}

interface State {
	baseFactor: string;
	parsedBaseFactor: number;
}

export default class RatesForm extends PureComponent<Props, State> {
	static defaultProps = {
		styles,
	};

	state = {
		baseFactor: '1',
		parsedBaseFactor: 1,
	};

	resolveExchangeRates(): Rate[] {
		const { selectedRates, baseCurrency } = this.props;
		return selectedRates.baseCurrency === baseCurrency
			? selectedRates.exchangeRates
			: selectedRates.calculateExchangeRatesFor(baseCurrency);
	}
	baseFactorChanged(baseFactor: string) {
		const { selectedRates, changedRates, onChange } = this.props;
		const parsedBaseFactor = parseHumanFloat(baseFactor) || 1;
		const nextChangedRates: {
			[key: string]: Factor;
		} = {};

		selectedRates.exchangeRates.forEach((rate) => {
			const changedRate = changedRates[rate.currency] || {
				raw: rate.factor,
				parsed: Number(rate.factor),
				isValid: true,
			};
			if (changedRate.parsed !== null && changedRate.parsed !== undefined) {
				const factor = changedRate.parsed * parsedBaseFactor;
				nextChangedRates[rate.currency] = {
					raw: String(factor),
					parsed: changedRate.parsed,
					isValid: changedRate.isValid,
				};
			} else {
				nextChangedRates[rate.currency] = { ...changedRate };
			}
		});

		this.setState({ baseFactor, parsedBaseFactor });
		onChange(nextChangedRates);
	}

	rateChanged(rate: Rate, factor: string) {
		const {
			props: { changedRates, onChange },
			state: { parsedBaseFactor },
		} = this;
		const parsedFactor = parseHumanFloat(factor);

		onChange({
			...changedRates,
			[rate.currency]: {
				raw: factor,
				parsed: parsedFactor ? parsedFactor / parsedBaseFactor : null,
				isValid: parsedFactor !== null && isFactorValid(parsedFactor),
			},
		});
	}

	render() {
		const {
			props: { changedRates, baseCurrency, styles, onBaseCurrencyChange },
			state: { baseFactor, parsedBaseFactor },
		} = this;
		const inputStyles = { default: styles.input };
		const suffixButtonStyles = { button: styles.inputSuffixButton };

		return (
			<Fragment>
				<p className={styles.paragraph}>
					<strong>Exchange rates according</strong> to selected list <strong>converted to {baseCurrency}</strong>. You{' '}
					<strong>can change exchange rates</strong> and save them as <strong>custom list</strong>.
				</p>

				<div className={styles.inputItem}>
					<Input
						styles={inputStyles}
						value={baseFactor}
						onChange={(event) => this.baseFactorChanged(event.currentTarget.value)}
					/>
					<p className={styles.inputSuffix}>
						<strong>{baseCurrency}</strong>
					</p>
				</div>
				<div className={styles.separator}>
					<span>OR</span>
				</div>
				{this.resolveExchangeRates().map((rate) => {
					const factor =
						changedRates[rate.currency] !== undefined
							? changedRates[rate.currency]
							: {
									raw: rate.factor,
									parsed: Number(rate.factor),
									isValid: true,
							  };

					return (
						<div className={styles.inputItem} key={rate.currency}>
							<Input
								isInvalid={!factor.isValid}
								styles={inputStyles}
								value={factor.raw}
								onChange={(event) => this.rateChanged(rate, event.currentTarget.value)}
							/>
							<p className={styles.inputSuffix}>
								<Button
									styles={suffixButtonStyles}
									title='Use as base currency'
									onClick={() => onBaseCurrencyChange(rate.currency)}
								>
									{rate.currency}
								</Button>
								1&nbsp;{rate.currency}
								{' = '}
								{factor.parsed && parsedBaseFactor ? formatFactor(1 / factor.parsed) : '?'}
								&nbsp;
								{baseCurrency}
							</p>
						</div>
					);
				})}
			</Fragment>
		);
	}
}
